import produce from 'immer';
import { ceil, omit, reduce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { addNumbersOrNull } from '../../../../../helpers/numberHelpers';
import { DataForDesignDrawingsManifold } from '../../../../designs/data-for-design-drawings/dataForDesignDrawingsResponse';
import {
  HeatSourceFormModel,
  HeatSourceResponse,
  PipeFormModel,
  PipeResponse,
  PumpFormModel,
  PumpResponse,
} from '../../GetDataForQuoteOrDesignForm';
import {
  JunctionTreeNode,
  NewTreeNode,
  NULL_CHILD,
  PumpTreeNode,
  TreeNode,
  TreeNodeType,
  TreeNodeWithStats,
} from '../treeNode';

export const ROOT_ID = 'ROOT';

export const createInitialTreeWithHeatPumpRoot = (): Tree => {
  return { [ROOT_ID]: { ...initialRoot, id: ROOT_ID } };
};

export const getManifoldFlowRate = (rooms: Array<RoomStatsForTree>): number | null => {
  if (!rooms || rooms.length === 0) {
    return null;
  }
  const roomsHaveSameManifold = rooms.every(room => room.manifoldIndex === rooms[0].manifoldIndex);
  if (!roomsHaveSameManifold) {
    throw new Error('All rooms must have the same manifold index');
  }

  const flowRatesToReduce: Array<number> = rooms.map(room =>
    room.flowRateInLitresPerMinute != null && room.numberOfPorts != null
      ? ceil(room.flowRateInLitresPerMinute / room.numberOfPorts, 1) * room.numberOfPorts
      : 0,
  );
  return reduce(flowRatesToReduce, (runningFlowRate, flowRate) => flowRate + runningFlowRate, 0);
};

export const getMaxDepth = (tree: Tree) => {
  const leafArray = getLeavesOfTree(tree);
  return Math.max(...leafArray.map(node => node.depth));
};

export const addNodeToTreeWithoutStats = (tree: Tree, node: NewTreeNode, parentId: string, isFirstChild: boolean) => {
  const id = uuidv4();
  return produce(tree, draft => {
    const parent = draft[parentId];

    if (parent.nodeType === TreeNodeType.Junction && !isFirstChild) {
      if (node.nodeType !== TreeNodeType.Manifold) {
        if (parent.secondChild.id) {
          node.firstChild = { ...parent.secondChild };
        }
      }
      parent.secondChild.id = id;
    } else if (parent.nodeType !== TreeNodeType.Manifold) {
      if (node.nodeType !== TreeNodeType.Manifold) {
        if (parent.firstChild.id) {
          node.firstChild = { ...parent.firstChild };
        }
      }

      parent.firstChild.id = id;
    }

    draft[id] = { ...node, id };
  });
};

export const smartMarkNodeForRemovalFromTreeWithoutStats = (
  tree: Tree,
  node: PumpTreeNode | JunctionTreeNode,
  replaceWithFirstChild = true,
) =>
  markNodes(tree, [
    node.id,
    ...(node.nodeType === TreeNodeType.Junction ? getOrphanedChildren(tree, node, replaceWithFirstChild) : []),
  ]);

export const smartRemoveEntryFromTreeWithoutStats = (
  tree: Tree,
  node: PumpTreeNode | JunctionTreeNode,
  replaceWithFirstChild = true,
) => {
  const newTree = omit(tree, getNodeIdsMarkedForDeletion(tree));
  const child = node.nodeType === TreeNodeType.Junction && !replaceWithFirstChild ? node.secondChild : node.firstChild;

  return produce(newTree, draft => {
    const parent = getParent(draft, node);
    if (parent && parent.nodeType === TreeNodeType.Junction && parent.secondChild.id === node.id) {
      parent.secondChild.id = child.id;
    } else if (parent && parent.nodeType !== TreeNodeType.Manifold) {
      parent.firstChild.id = child.id;
    }
  });
};

const getOrphanedChildren = (tree: Tree, treeNode: JunctionTreeNode, replaceWithFirstChild: boolean): Array<string> => {
  if (replaceWithFirstChild && treeNode.secondChild.id) {
    return [...getChildrenIds(tree, treeNode.secondChild.id), treeNode.secondChild.id];
  }

  if (!replaceWithFirstChild && treeNode.firstChild.id) {
    return [...getChildrenIds(tree, treeNode.firstChild.id), treeNode.firstChild.id];
  }

  return [];
};

export const deleteNodeAndChildrenFromTreeWithoutStats = (tree: Tree, nodeId: string) => {
  const nodesForDeletion = getNodeIdsMarkedForDeletion(tree);

  const parentId = getParent(tree, tree[nodeId])?.id;
  const newTree: Tree = omit(tree, nodesForDeletion);
  return produce(newTree, draft => {
    const parent = parentId && draft[parentId];
    if (parent && parent.nodeType === TreeNodeType.Junction && parent.secondChild.id === nodeId) {
      parent.secondChild = { ...NULL_CHILD };
    } else if (parent && parent.nodeType !== TreeNodeType.Manifold) {
      parent.firstChild = { ...NULL_CHILD };
    }
  });
};

export const markNodeAndChildrenForDeletionForTreeWithoutStats = (tree: Tree, nodeId: string) => {
  const nodesForMarking = [...getChildrenIds(tree, nodeId), nodeId];
  return markNodes(tree, nodesForMarking);
};

export const markNodes = (tree: Tree, idsForMarking: Array<string>) =>
  produce(tree, draft => {
    idsForMarking.forEach(id => {
      const node = draft[id];
      if (node) {
        node.markedForDeletion = true;
        if (node.nodeType !== TreeNodeType.Manifold) {
          node.firstChild.exitPipe.markedForDeletion = true;
        }
        if (node.nodeType === TreeNodeType.Junction) {
          node.secondChild.exitPipe.markedForDeletion = true;
        }
      }
    });
  });

export const unmarkForDeletionAllNodesInTree = (tree: Tree) =>
  produce(tree, draft => {
    Object.values(draft).forEach(node => {
      node.markedForDeletion = false;
      if (node.nodeType !== TreeNodeType.Manifold) {
        node.firstChild.exitPipe.markedForDeletion = false;
      }
      if (node.nodeType === TreeNodeType.Junction) {
        node.secondChild.exitPipe.markedForDeletion = false;
      }
    });
  });

const getLeavesOfTree = (tree: Tree): Array<NodeWithDepth> => {
  return leavesInNode(tree, ROOT_ID, 1);
};

const getParent = (tree: Tree, treeNode: TreeNode): TreeNode | null => {
  const result = Object.values(tree).find(
    node =>
      node.nodeType !== TreeNodeType.Manifold &&
      (node.firstChild.id === treeNode.id ||
        (node.nodeType === TreeNodeType.Junction && node.secondChild.id === treeNode.id)),
  );

  return result ?? null;
};

const getNodeIdsMarkedForDeletion = (tree: Tree) =>
  Object.entries(tree)
    .filter(([, node]) => node.markedForDeletion)
    .map(([key]) => key);

const getChildrenIds = (tree: Tree, id: string | null): Array<string> => {
  if (!id || !tree[id]) {
    return [];
  }

  const node = tree[id];

  switch (node.nodeType) {
    case TreeNodeType.Manifold:
      return [id];

    case TreeNodeType.Pump:
    case TreeNodeType.HeatSource:
      if (!node.firstChild.id) {
        return [id];
      }

      return [...getChildrenIds(tree, node.firstChild.id), node.firstChild.id];

    case TreeNodeType.Junction:
      if (!node.firstChild.id && !node.secondChild.id) {
        return [id];
      }

      return (node.firstChild.id ? [...getChildrenIds(tree, node.firstChild.id), node.firstChild.id] : []).concat(
        node.secondChild.id ? [...getChildrenIds(tree, node.secondChild.id), node.secondChild.id] : [],
      );
  }
};

const leavesInNode = (tree: Tree, id: string, depth: number): Array<NodeWithDepth> => {
  if (!tree[id]) {
    return [];
  }
  const node = tree[id];

  switch (node.nodeType) {
    case TreeNodeType.Junction:
      if (!node.firstChild.id && !node.secondChild.id) {
        return [{ node, depth }];
      }

      return (node.firstChild.id ? leavesInNode(tree, node.firstChild.id, depth + 1) : []).concat(
        node.secondChild.id ? leavesInNode(tree, node.secondChild.id, depth + 1) : [],
      );

    case TreeNodeType.HeatSource:
    case TreeNodeType.Pump:
      return node.firstChild.id ? leavesInNode(tree, node.firstChild.id, depth + 1) : [{ node, depth }];

    case TreeNodeType.Manifold:
      return [{ node, depth }];
  }
};

export const countJunctionsInNode = (tree: Tree, id: string | null): number => {
  if (!id || !tree[id]) {
    return 0;
  }

  const node = tree[id];

  switch (node.nodeType) {
    case TreeNodeType.Junction:
      return countJunctionsInNode(tree, node.firstChild.id) + countJunctionsInNode(tree, node.secondChild.id) + 1;

    case TreeNodeType.HeatSource:
    case TreeNodeType.Pump:
      return countJunctionsInNode(tree, node.firstChild.id);

    case TreeNodeType.Manifold:
      return 0;
  }
};

export const getFlowRate = (tree: TreeWithStats, nodeId: string | null): number | null => {
  if (!nodeId || !tree[nodeId]) {
    return null;
  }

  const node = tree[nodeId];

  switch (node.nodeType) {
    case TreeNodeType.Manifold:
      return node.stats.flowRateInLitresPerMinute;

    case TreeNodeType.HeatSource:
    case TreeNodeType.Pump:
      if (!node.firstChild.id) {
        return null;
      }
      return getFlowRate(tree, node.firstChild.id);

    case TreeNodeType.Junction:
      return addNumbersOrNull(
        node.stats.firstChildExitPipe.flowRateInLitresPerMinute,
        node.stats.secondChildExitPipe.flowRateInLitresPerMinute,
      );
  }

  return getFlowRate(tree, node);
};

export const getStatsData = (
  manifolds: Array<DataForDesignDrawingsManifold>,
  unitPumps: Array<PumpResponse>,
  manifoldPumps: Array<PumpResponse>,
  heatSources: Array<HeatSourceResponse>,
  pipes: Array<PipeResponse>,
) => {
  const rooms: Array<RoomStatsForTree> = manifolds.flatMap((manifold, index) =>
    manifold.rooms.map(room => ({
      manifoldIndex: index,
      flowRateInLitresPerMinute: room.heatLossAndHeatOutput?.flowRateInLitresPerMinute ?? null,
      pipeDiameterInMillimetres: room.pipeDiameterInMillimetres ?? null,
      pressureLossPerCoil: room.heatLossAndHeatOutput?.pressureLossPerCoil ?? null,
      numberOfPorts: room.numberOfPorts,
    })),
  );

  const pumpsByManifoldIndex = manifolds.map(manifold =>
    manifold.pumpId != null ? unitPumps.find(pump => pump.partId === manifold.pumpId) ?? null : null,
  );

  return {
    rooms,
    referenceData: {
      unitPumpOptions: unitPumps,
      manifoldPumpOptions: manifoldPumps,
      heatSourceOptions: heatSources,
      pipeOptions: pipes,
    },
    pumpsByManifoldIndex,
  };
};

type NodeWithDepth = {
  node: TreeNode;
  depth: number;
};

export type Tree = { [key: string]: TreeNode };
export type TreeWithStats = { [key: string]: TreeNodeWithStats };
export type RoomStatsForTree = {
  manifoldIndex: number;
  flowRateInLitresPerMinute: number | null;
  pipeDiameterInMillimetres: number | null;
  pressureLossPerCoil: number | null;
  numberOfPorts: number | null;
};
export type StatsData = {
  pumpsByManifoldIndex: Array<PumpFormModel | PumpResponse | null>;
  rooms: Array<RoomStatsForTree> | null;
  referenceData: ReferenceDataForTree;
};

export type ReferenceDataForTree = {
  unitPumpOptions: Array<PumpFormModel | PumpResponse | null>;
  manifoldPumpOptions: Array<PumpFormModel | PumpResponse | null>;
  pipeOptions: Array<PipeFormModel | PipeResponse | null>;
  heatSourceOptions: Array<HeatSourceFormModel | HeatSourceResponse | null>;
};

const initialRoot: NewTreeNode = {
  markedForDeletion: false,
  nodeType: TreeNodeType.HeatSource,
  fieldData: { heatSource: 'Domestic Boiler & Pump' },
  firstChild: { ...NULL_CHILD },
};
